/**
 * Sticky Add-to-Cart Bar
 * Shows a fixed bottom bar when the main ATC button scrolls out of view.
 * Syncs variant selection via the theme's PUB_SUB_EVENTS.variantChange system.
 */
class StickyATC extends HTMLElement {
  connectedCallback() {
    const targetSelector = this.dataset.targetButton;
    if (!targetSelector) return;

    this.targetButton = document.querySelector(targetSelector);
    if (!this.targetButton) return;

    this._setupObserver();
    this._bindVariantSync();
    this._bindSubmit();
  }

  /**
   * IntersectionObserver on the main ATC button.
   * When the button is NOT intersecting (scrolled out), show the sticky bar.
   */
  _setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this.targetButton);
  }

  /**
   * Listen for variant changes via the theme's pub/sub system
   * and sync the sticky bar's variant select and button state.
   */
  _bindVariantSync() {
    this.stickySelect = this.querySelector('[data-sticky-variant]');
    this.stickySubmit = this.querySelector('[data-sticky-submit]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');

    // Subscribe to theme variant change events
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this._onVariantChange.bind(this)
      );
    }

    // When user changes the sticky bar's variant select, sync back to main form
    if (this.stickySelect) {
      this.stickySelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        this._syncMainFormVariant(variantId);
      });
    }
  }

  /**
   * Handle variant change events from the main product form.
   * Update sticky bar select, price, and button state.
   */
  _onVariantChange(event) {
    const { variant, sectionId } = event.data;

    // Only respond to events for our section
    const ourSection = this.dataset.sectionId;
    if (ourSection && sectionId !== ourSection) return;

    if (this.stickySelect) {
      this.stickySelect.value = variant ? variant.id : '';
    }

    if (this.stickyPrice && variant) {
      this.stickyPrice.textContent = Shopify.formatMoney
        ? Shopify.formatMoney(variant.price)
        : (variant.price / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: Shopify.currency?.active || 'USD',
          });
    }

    if (this.stickySubmit) {
      var span = this.stickySubmit.querySelector('span');
      if (variant && variant.available) {
        this.stickySubmit.removeAttribute('disabled');
        if (span) { span.textContent = window.variantStrings?.addToCart || 'Add to cart'; }
      } else {
        this.stickySubmit.setAttribute('disabled', 'disabled');
        if (span) { span.textContent = window.variantStrings?.soldOut || 'Sold out'; }
      }
    }
  }

  /**
   * Sync the selected variant back to the main product form's
   * variant-selects element by finding the matching option and dispatching
   * a change event.
   */
  _syncMainFormVariant(variantId) {
    const sectionId = this.dataset.sectionId;
    const mainSelect = document.querySelector(
      `#variant-selects-${sectionId} select, variant-selects[data-section="${sectionId}"] select`
    );
    // For themes that use fieldset radios (pill/swatch pickers) we find the
    // hidden input that matches, or fallback to dispatching on the variant-selects element.
    const variantSelects = document.querySelector(
      `variant-selects[data-section="${sectionId}"]`
    );

    // Find the variant data to get option values
    if (variantSelects) {
      const variantData = JSON.parse(
        variantSelects.querySelector('script[type="application/json"]')?.textContent || '[]'
      );
      const variant = variantData.find((v) => String(v.id) === String(variantId));

      if (variant) {
        // Update each option's input/select in the main form
        variant.options.forEach((optionValue, index) => {
          const select = variantSelects.querySelector(
            `select[name="options[${CSS.escape(variant.options[index])}]"], select:nth-of-type(${index + 1})`
          );
          const input = variantSelects.querySelector(
            `input[value="${CSS.escape(optionValue)}"]`
          );

          if (select) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  }

  /**
   * Clicking the sticky ATC button triggers the main form's submit button.
   */
  _bindSubmit() {
    const stickyBtn = this.querySelector('[data-sticky-submit]');
    if (!stickyBtn) return;

    stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.targetButton) {
        this.targetButton.click();
      }
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this._variantChangeUnsubscriber) {
      this._variantChangeUnsubscriber();
    }
  }
}

customElements.define('sticky-atc', StickyATC);
